// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { Account } from './account';
import { AddonBin } from './addon-bin';
import { CancellationRequest } from './cancellation-request';
import { CreditMemo } from './credit-memo';
import { InvoiceHeader } from './invoice-header';
import { JoDetail } from './jo-detail';
import { JoHeaderPull } from './jo-header-pull';
import { JoNote } from './jo-note';
import { JoStatus } from './jo-status';
import { Manifest } from './manifest';
import { ReturnRequest } from './return-request';
import { ShipFrequency } from './ship-frequency';

/// <code-import> Place custom imports between <code-import> tags
import _ from "lodash";
import { EntityPropertyAnnotation, EntityTypeAnnotation } from "../entity-type-annotation";
/// </code-import>

/// <module-code> Place module level code between <module-code> tags

/// </module-code>

export class JoHeader extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  static getEntityTypeAnnotation(): EntityTypeAnnotation {
    const propAnnotations = [
      // new EntityPropertyAnnotation('leadTimeToShipNumDays', {
      //   displayName: 'Lead Time to Ship (Days)',
      //   validators: [ BaseEntity.greaterThanZeroValidator ]
      // }),
      new EntityPropertyAnnotation("taxRate", {
        validators: [BaseEntity.createLessThanValidator(0.35)],
      }),
    ];

    return new EntityTypeAnnotation({
      validators: [],
      propertyAnnotations: propAnnotations,
    });
  }

  get shippingKey() {
    // doesAccountPayFreight id is added because we will not allow different doesAccountPayFreights to ship together.
    return (
      this.accountId.toString() +
      (this.shippingName ?? "") +
      "|" +
      (this.shippingAddress1 ?? "") +
      "|" +
      (this.shippingAddress2 ?? "") +
      "|" +
      (this.shippingAddress3 ?? "") +
      "|" +
      (this.shippingCity ?? "") +
      "|" +
      (this.shippingState ?? "") +
      "|" +
      (this.shippingZipCode ?? "") +
      "|" +
      this.doesAccountPayFreight +
      "|" +
      this.shipFrequencyId
    );
  }

  // conceptual Note interface
  getNotes() {
    return this.joNotes;
  }
  //

  // next three gets require that associated joDetails have been fetched.
  // getShipQty() {
  //   return _.sumBy(this.joDetails, jod => jod.shipQty);
  // }

  // getCancelAndReturnQty() {
  //   return _.sumBy(this.joDetails, jod => jod.cancelQty + jod.returnQty);
  // }

  getReturnQty() {
    return _.sumBy(this.joDetails, (jod) => jod.returnQty);
  }

  getCancelQty() {
    return _.sumBy(this.joDetails, (jod) => jod.cancelQty);
  }

  getOrderQty() {
    return _.sumBy(this.joDetails, (jod) => jod.orderQty);
  }

  getFormalEmployeeName() {
    return !!this.empLastName ? this.empLastName.trim() + ", " + this.empFirstName?.trim() : this.shippingName?.trim() ?? this.billingName.trim() ?? "";
  }

  /** Return total creditMemoAmt of all credit memos applied to this JoHeader.
   * Requires that joHeader.returnRequests.returnRequestDetails.creditMemos have been fetched. */
  getCreditMemoTotal() {
    const sum = this.returnRequests.reduce(
      (sum, x) => x.returnRequestDetails.reduce((sum, x) => x.creditMemos.reduce((sum, x) => sum + x.creditMemoAmt, 0), 0),
      0
    );
    return sum;
  }

  /** Return total net amounts of all invoices (so far) on the job order.  May be negative if an invoice had a large prepayment. */
  getInvoiceTotal() {
    const sum = this.invoiceHeaders.reduce((sum, x) => sum + x.calcTotalAmt(), 0);
    return +sum.toFixed(2);
  }

  getJoShipDate() {
    return this.origExpectedShipDate;
  }

  // TODO: manage idx for 'When Bin is Full' in a better way than this
  getAdjustedExpectedShipDate() {
    let idx = 0;
    if (!this.shipFrequency || this.shipFrequency.id === 0) {
      idx = 0;
    } else if (this.shipFrequency.id === 99) {
      idx = 4;
    } else {
      idx = this.shipFrequency.id;
    }
    return this.shipFrequency.rRuleByWeekDay;
  }
  /// </code>

  // Generated code. Do not place code below this line.
  /** Int32 key */
  id!: number;
  /** Int32 FK account */
  accountId!: number;
  /** Boolean */
  allowInventory!: boolean;
  /** Boolean */
  allowPartialShipments!: boolean;
  /** String(50) */
  billingAddress1!: string;
  /** String(50) */
  billingAddress2?: string;
  /** String(50) */
  billingAddress3?: string;
  /** String(50) */
  billingCity!: string;
  /** String(100) */
  billingEmail?: string;
  /** String(4) */
  billingExt?: string;
  /** String(50) */
  billingName!: string;
  /** String(10) */
  billingState?: string;
  /** String(25) */
  billingTelephone?: string;
  /** String(10) */
  billingZipCode!: string;
  /** String(50) */
  carrierAccountName?: string;
  /** String(50) */
  carrierAccountNumber?: string;
  /** String(10) */
  carrierAccountPostalCode?: string;
  /** Boolean */
  chargeAddonsOnReissuance!: boolean;
  /** DateTimeOffset */
  crtnTs!: Date;
  /** String(30) */
  customerPurchaseOrderNumber?: string;
  /** Decimal */
  discountAmt?: number;
  /** Boolean */
  doesAccountPayFreight!: boolean;
  /** String(20) */
  empCellNumber?: string;
  /** String(100) */
  empEmail?: string;
  /** String(30) */
  empFirstName?: string;
  /** String(30) */
  empLastName?: string;
  /** String(4) */
  empSalutation?: string;
  /** Decimal */
  handlingChargePerOrderAmt!: number;
  /** Decimal */
  handlingChargePerShipmentAmt!: number;
  /** String(150) */
  inventoryProgramName?: string;
  /** Boolean */
  isAccountOwnedOrder!: boolean;
  /** Boolean */
  isPlacedOnBehalfOfUser!: boolean;
  /** DateTime */
  joDate!: Date;
  /** String(7) FK joStatus */
  joStatusId!: string;
  /** Int32 */
  leadTimeToShipNumDays!: number;
  /** Int32 FK manifest */
  manifestId?: number;
  /** Int32 */
  oLD_CUS_KEY?: number;
  /** Int32 */
  oLD_PRO_KEY!: number;
  /** DateTime */
  origExpectedShipDate!: Date;
  /** String(50) */
  pCardCustomerProfileId?: string;
  /** String(100) */
  pCardName?: string;
  /** String(50) */
  pCardPaymentProfileId?: string;
  /** String(20) */
  placedByCellNumber?: string;
  /** String(100) */
  placedByEmail?: string;
  /** String(30) */
  placedByFirstName?: string;
  /** String(30) */
  placedByLastName?: string;
  /** String(50) */
  portalName?: string;
  /** Decimal */
  prepaymentAmt?: number;
  /** String(100) */
  prepaymentName?: string;
  /** String(50) */
  prepaymentSalesReceiptId?: string;
  /** String(50) */
  prepaymentTransactionId?: string;
  /** String(150) */
  programName?: string;
  /** String(150) */
  programUserGroupName?: string;
  /** Guid */
  proximityJoHeaderId?: string;
  /** Binary */
  rowVersion!: any;
  /** Int32 FK shipFrequency */
  shipFrequencyId!: number;
  /** Int32 */
  shipPriorityOrder!: number;
  /** String(50) */
  shippingAddress1?: string;
  /** String(50) */
  shippingAddress2?: string;
  /** String(50) */
  shippingAddress3?: string;
  /** String(50) */
  shippingCity?: string;
  /** String(4) */
  shippingExt?: string;
  /** String(50) */
  shippingName?: string;
  /** String(10) */
  shippingState?: string;
  /** String(25) */
  shippingTelephone?: string;
  /** String(10) */
  shippingZipCode?: string;
  /** Boolean */
  shouldChargeFreightOnFirstShipmentOnly!: boolean;
  /** Decimal */
  taxRate!: number;
  /** FK accountId */
  account!: Account;
  /** Inv FK joHeaderId */
  addonBins!: AddonBin[];
  /** Inv FK joHeaderId */
  cancellationRequests!: CancellationRequest[];
  /** Inv FK joHeaderId */
  creditMemos!: CreditMemo[];
  /** Inv FK joHeaderId */
  invoiceHeaders!: InvoiceHeader[];
  /** Inv FK joHeaderId */
  joDetails!: JoDetail[];
  /** Inv FK joHeaderId */
  joHeaderPull!: JoHeaderPull;
  /** Inv FK joHeaderId */
  joNotes!: JoNote[];
  /** FK joStatusId */
  joStatus!: JoStatus;
  /** FK manifestId */
  manifest!: Manifest;
  /** Inv FK joHeaderId */
  returnRequests!: ReturnRequest[];
  /** FK shipFrequencyId */
  shipFrequency!: ShipFrequency;
}

